import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cn } from "../../lib/utils";
import { Check, Minus, AlertTriangle } from "lucide-react";

const Checkbox = React.forwardRef(
  (
    {
      className,
      checked,
      indeterminate,
      disabled,
      error,
      focused,
      variant = "default",
      label,
      labelClassName,
      checkboxClasses,
      errorMessage,
      ...props
    },
    ref
  ) => {
    const getVariantClasses = () => {
      const baseClasses =
        "h-4 w-4 rounded border flex items-center justify-center transition-all duration-200";

      if (disabled) {
        return cn(
          baseClasses,
          "border-gray-300 bg-gray-100 cursor-not-allowed",
          checked || indeterminate ? "bg-gray-300" : ""
        );
      }

      if (error) {
        return cn(
          baseClasses,
          "border-red-500 hover:border-red-500 focus:border-red-500 bg-white",
          checked || indeterminate ? "bg-red-500 border-red-500" : "",
          focused ? "ring-2 ring-red-200 ring-offset-2" : ""
        );
      }

      if (focused) {
        const focusClasses =
          variant === "secondary"
            ? "border-purple-500 ring-2 ring-purple-200 ring-offset-2"
            : "border-secondary-2 ring-2 ring-secondary-2/20 ring-offset-2";
        const bgClass =
          variant === "secondary"
            ? checked || indeterminate
              ? "bg-purple-500"
              : "bg-white"
            : checked || indeterminate
            ? "bg-secondary-2"
            : "bg-white";

        return cn(baseClasses, focusClasses, bgClass);
      }

      // Default states
      if (checked || indeterminate) {
        const checkedClasses =
          variant === "secondary"
            ? "bg-purple-500 border-purple-500"
            : "bg-secondary-2 border-secondary-2";
        return cn(baseClasses, checkedClasses);
      }

      return cn(baseClasses, "border-gray-400 bg-white hover:border-gray-500");
    };

    const getIconColor = () => {
      if (disabled) return "text-gray-500";
      return "text-white";
    };

    return (
      <div className={cn("flex flex-col space-y-1", className)}>
        <div className="flex items-center space-x-3">
          <CheckboxPrimitive.Root
            ref={ref}
            className={cn(getVariantClasses(), checkboxClasses)}
            checked={indeterminate ? "indeterminate" : checked}
            disabled={disabled}
            aria-invalid={error}
            aria-describedby={
              error && errorMessage ? `${props.id}-error` : undefined
            }
            {...props}
          >
            <CheckboxPrimitive.Indicator className="flex items-center justify-center">
              {indeterminate ? (
                <Minus className={cn("h-3 w-3", getIconColor())} />
              ) : (
                <Check className={cn("h-3 w-3", getIconColor())} />
              )}
            </CheckboxPrimitive.Indicator>
          </CheckboxPrimitive.Root>

          {label && (
            <label
              htmlFor={props.id}
              className={cn(
                "text-sm font-medium leading-none peer-disabled:cursor-not-allowed",
                disabled ? "text-gray-400" : "text-gray-900",
                labelClassName || ""
              )}
            >
              {label}
            </label>
          )}
        </div>
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";

export { Checkbox };
